import { notify } from "../components/NotificationPlugin";

type PunctuationRule = {
  after?: boolean;
  before?: boolean;
  condition?: 'afterSpaceOnly' | 'if40CharsBefore' | 'debatable' | 'never';
};

type PunctuationRules = {
  [key: string]: PunctuationRule;
};

const punctuationRules: PunctuationRules = {
  '.': { after: true, condition: 'afterSpaceOnly' },
  ',': { after: true, condition: 'if40CharsBefore' },
  '?': { after: true },
  '!': { after: true },
  '...': { after: true },
  '(': { before: true },
  ')': { after: true },
  '„': { before: true },
  '"': { after: true },
  ':': { after: true, condition: 'debatable' },
  '-': { condition: 'never' }
};

// adaugare "a", "la", 
// "punct de vedere" // nu se poate rupe

const specialWords: string[] = [

  'dacă', 'și', 'care', 'deoarece', 'pentru că', 'fiindcă', 'ca să', 'iar',
  'ca', 'ci', 'sau', 'ori', 'că', 'încât', 'astfel încât', 'nici', 'fie',
  'dar', 'însă', 'așadar', 'precum', 'să', 'unde', 'cum', 'deși', 'căci',
  'cum că', 'după cum', 'se', 'în care', 'pe care', 'după care', 'pentru că',
  'așa că', 'am să', 'o să', 'să fie', 'ca să', 'decât ca', 'doar ca'
];

const isWordBoundary = (text: string, index: number): boolean => {
  if (index === 0 || index === text.length) return true;
  return /\s/.test(text[index - 1]) || /\s/.test(text[index]);
};

/*

const shouldSplit = (text: string, index: number, enforceRules: boolean): boolean => {
  if (!isWordBoundary(text, index)) return false;
  if (!enforceRules) return true;
  const textBefore = text.slice(0, index);
  const textAfter = text.slice(index);
  for (const [punct, rule] of Object.entries(punctuationRules)) {
    if (textBefore.endsWith(punct)) {
      if (rule.condition === 'never') return false;
      if (rule.after === false)
      {
        return false;
      }
      if (rule.condition === 'afterSpaceOnly') {
        return textAfter.startsWith(' ');
      }
      if (rule.condition === 'if40CharsBefore') {
        const relevantText = textBefore.slice(Math.max(0, textBefore.length - 40));
        return relevantText.length >= 40;
      }
      if (rule.after === true) return true;
    }

    if (textAfter.startsWith(punct)) {
      if (rule.condition === 'never')
      {
        return false;
      }
      if (rule.before === false) return false;
      if (rule.before === true) return true;
    }
  }

  // Check for special words
  for (const word of specialWords) {
    if (text.slice(index).startsWith(word + ' ') || text.slice(index).startsWith(word + '\n')) {
      if (index === 0) return false;
      const textBefore = text.slice(0, index).trim();
      if (textBefore.split(/\s+/).length <= 1) return false;
    }
  }

  for (const phrase of specialWords.filter(word => word.includes(' '))) {
    const startIndex = Math.max(0, index - phrase.length);
    if (text.slice(startIndex, index + phrase.length).includes(phrase)) {
      return false;

    }
  }

  return true;
};
*/

const shouldSplit = (text: string, index: number, enforceRules: boolean, verbose = false): boolean => {
  if (!isWordBoundary(text, index))
  {
    if (verbose) notify('danger', `Can't split in the middle of a word`);
    return false;
  }
  if (!enforceRules) return true;
  const textBefore = text.slice(0, index);
  const textAfter = text.slice(index);
  
  for (const [punct, rule] of Object.entries(punctuationRules)) {
    if (textBefore.endsWith(punct)) {
      if (rule.condition === 'never') {
        if (verbose) notify('danger', `Cannot split after due to punctuation rule`,` "${punct}"`);
        return false;
      }
      if (rule.after === false) {
        if (verbose) notify('danger', `Cannot split after as specified by rule`,` "${punct}"`);
        return false;
      }
      if (rule.condition === 'afterSpaceOnly' && !textAfter.startsWith(' ')) {
        if (verbose) notify('danger', `Can only split after when followed by space`,` "${punct}"`);
        return false;
      }
      if (rule.condition === 'if40CharsBefore') {
        const relevantText = textBefore.slice(Math.max(0, textBefore.length - 40));
        if (relevantText.length < 40) {
          if (verbose) notify('danger', `Requires 40 characters before split`,` "${punct}"`);
          return false;
        }
      }
      if (rule.after === true) return true;
    }
    
    if (textAfter.startsWith(punct)) {
      if (rule.condition === 'never') {
        if (verbose) notify('danger', `Cannot split before due to punctuation rule`,` "${punct}"` );
        return false;
      }
      if (rule.before === false) {
        if (verbose) notify('danger', `Cannot split before as specified by rule`,` "${punct}"`);
        return false;
      }
      if (rule.before === true) return true;
    }
  }

  // Check for special words
  for (const word of specialWords) {
    if ((text.slice(index).startsWith(word + ' ') || 
         text.slice(index).startsWith(word + '\n'))) {
      if (index === 0) {
        if (verbose) notify('danger', `Cannot split at start of special word`,` "${word}"`);
        return false;
      }
      const textBefore = text.slice(0, index).trim();
      if (textBefore.split(/\s+/).length <= 1) {
        if (verbose) notify('danger', `Cannot split with single word before special word`,` "${word}"`);
        return false;
      }
    }
  }

  for (const phrase of specialWords.filter(word => word.includes(' '))) {
    const startIndex = Math.max(0, index - phrase.length);
    const testText = text.slice(startIndex, index + phrase.length);
    const phraseStart = testText.indexOf(phrase);
    
    if (phraseStart !== -1 && phraseStart < (index - startIndex) && (index - startIndex) < phraseStart + phrase.length) {
      if (verbose) notify('danger', `Cannot split within special phrase`,` "${phrase}"`);
      return false;
    }
  }
  
  return true;
};

export { punctuationRules, specialWords, shouldSplit };
